import React from 'react';
import { getBackendUrl } from '@/config';
import { useNavigate } from 'react-router-dom';
import { Search, User, Menu, LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { useAuth } from '@/contexts/AuthContext';
import { ThemeToggle } from '@/components/shared/ThemeToggle';

export const TopNavbar = ({ title = 'Dashboard', user, onMenuClick, showSearch = true }) => {
  const { logout } = useAuth();
  const navigate = useNavigate();
  // Safe destructuring with fallbacks when user is null/undefined
  const safeUser = user || {};
  const { first_name = '', last_name = '', name = '', email = 'user@kosha.com', role = 'client', profile_image = '' } = safeUser;
  // Build display name: prefer first+last, then just first, then name field, then fallback to 'User'
  const displayName = first_name 
    ? (last_name ? `${first_name} ${last_name}` : first_name)
    : (name || 'User');
  const initials = displayName.split(' ').map(n => n[0]).join('').toUpperCase() || 'U';
  // Capitalize role for display
  const displayRole = role ? role.charAt(0).toUpperCase() + role.slice(1) : 'Client';
  
  // Build profile image URL if it exists
  const profileImageUrl = profile_image && profile_image.length > 0
    ? (profile_image.startsWith('/') 
        ? `${getBackendUrl()}${profile_image}`
        : profile_image)
    : null;

  const handleLogout = () => {
    logout();
  };

  return (
    <header className="sticky top-0 z-40 border-b bg-card shadow-material-sm">
      <div className="flex h-16 items-center gap-4 px-6">
        {/* Mobile menu button */}
        <Button
          variant="ghost"
          size="icon"
          className="lg:hidden"
          onClick={onMenuClick}
        >
          <Menu className="h-5 w-5" />
        </Button>

        {/* Page title */}
        <h1 className="text-xl font-semibold text-foreground">{title}</h1>

        {/* Search bar */}
        {showSearch && (
          <div className="ml-auto hidden w-full max-w-md md:block">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Search..."
                className="pl-10"
              />
            </div>
          </div>
        )}

        {/* Right section */}
        <div className="ml-auto flex items-center gap-4">
          {/* Theme Toggle */}
          <ThemeToggle />

          {/* User menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="gap-2">
                <Avatar className="h-8 w-8">
                  {profileImageUrl && <AvatarImage src={profileImageUrl} alt={displayName} />}
                  <AvatarFallback className="bg-primary text-primary-foreground">
                    {initials}
                  </AvatarFallback>
                </Avatar>
                <div className="hidden text-left md:block">
                  <p className="text-sm font-medium">{displayName}</p>
                  <p className="text-xs text-muted-foreground">{displayRole}</p>
                </div>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>My Account</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/profile')}>
                <User className="mr-2 h-4 w-4" />
                Profile
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate('/profile')}>Settings</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-destructive" onClick={handleLogout}>
                <LogOut className="mr-2 h-4 w-4" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
};
